import { type RoleMatrixCell, TEAM_ROLE_MATRIX } from '@fos/shared/labels';
import { Check } from 'lucide-react';
import { Section } from '../../../../ui/Section';
import { Table, TBody, Td, THead, Th, Tr } from '../../../../ui/Table';
import { useIsPhone } from '../../../mobile';

function Cell({ value }: { value: RoleMatrixCell }) {
  if (value === true) {
    return (
      <>
        <Check size={16} strokeWidth={2} aria-hidden className="text-success" />
        <span className="sr-only">tak</span>
      </>
    );
  }
  if (value === false) {
    return (
      <>
        <span aria-hidden className="text-fg-muted">
          —
        </span>
        <span className="sr-only">nie</span>
      </>
    );
  }
  return <span className="text-caption text-fg-secondary">{value}</span>;
}

const COLUMNS = [
  { key: 'admin', label: 'Admin' },
  { key: 'manager', label: 'Manager' },
  { key: 'user', label: 'Użytkownik' },
] as const;

/**
 * „Role i dostęp” (Figma 3034:4305): co daje każda rola. Statyczna ściąga pod listą zespołu. Telefon (lifting
 * 17.10.2026): zamiast tabeli 560 px przewijanej w bok — wiersz na uprawnienie z trzema rolami pod spodem.
 */
export function RoleMatrix() {
  const phone = useIsPhone();
  return (
    <Section className="mt-6" aria-labelledby="role-matrix">
      <h3 id="role-matrix" className="text-body-s font-semibold text-fg">
        Role i dostęp
      </h3>
      {phone ? (
        <ul className="mt-3" aria-label="Role i dostęp">
          {TEAM_ROLE_MATRIX.map((row) => (
            <li key={row.label} className="border-b border-line py-2.5 last:border-b-0">
              <p className="text-body-s text-fg">{row.label}</p>
              <dl className="mt-1.5 grid grid-cols-3 gap-2">
                {COLUMNS.map((c) => (
                  <div key={c.key} className="flex min-w-0 flex-col gap-0.5">
                    <dt className="truncate text-caption text-fg-muted">{c.label}</dt>
                    <dd className="flex min-h-5 items-center">
                      <Cell value={row[c.key]} />
                    </dd>
                  </div>
                ))}
              </dl>
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-3 overflow-hidden rounded-xl border border-line">
          <Table aria-labelledby="role-matrix" className="min-w-[560px]">
            <THead className="bg-surface">
              <Tr>
                <Th className="h-9 px-4 first:pl-4">Uprawnienie</Th>
                {COLUMNS.map((c) => (
                  <Th key={c.key} className="w-[142px] px-2">
                    {c.label}
                  </Th>
                ))}
              </Tr>
            </THead>
            <TBody>
              {TEAM_ROLE_MATRIX.map((row) => (
                <Tr key={row.label} className="last:border-b-0">
                  <Td className="h-[42px] px-4 first:pl-4">{row.label}</Td>
                  {COLUMNS.map((c) => (
                    <Td key={c.key} className="h-[42px] px-2">
                      <Cell value={row[c.key]} />
                    </Td>
                  ))}
                </Tr>
              ))}
            </TBody>
          </Table>
        </div>
      )}
    </Section>
  );
}
